import { buildComparableKey } from './highlightingUtils'

/**
 * Class responsible for generating PDF annotations from rule data and PDF parsing results
 */
export class AnnotationGenerator {
  constructor(data, pdfParsingResult, globalFields, options = { extractAllRuleDataPoints: false }) {
    this.data = data
    this.pdfParsingResult = pdfParsingResult
    this.globalFields = globalFields

    // Initialize result containers
    this.idToPage = {}
    this.highlightedIds = new Set()
    this.annotationsObject = {}
    this.comparableToPage = {}

    // Initialize data structures
    this.pathsToHighlight = new Set()
    this.pathToDataPoints = new Map()
    this.salesComparableMap = {}

    this.options = options
  }

  /**
   * Main method to generate annotations
   */
  generate() {
    if (!this.hasValidData()) {
      return this.getEmptyResult()
    }

    this.extractRuleDataPoints()
    this.processDataPoints()
    this.processPdfFields()

    return {
      annotations: this.annotationsObject,
      fieldIdToPage: this.idToPage,
      highlightedFieldIds: this.highlightedIds,
      comparableToPage: this.comparableToPage,
    }
  }

  /**
   * Check if required data is available
   */
  hasValidData() {
    return this.pdfParsingResult?.result?.pdf_page_fields && this.globalFields?.length
  }

  /**
   * Return empty result structure
   */
  getEmptyResult() {
    return {
      annotations: {},
      fieldIdToPage: {},
      highlightedFieldIds: new Set(),
      comparableToPage: {},
    }
  }


  extractRuleDataPoints() {
    let filteredRuleDataPoints = this.data

    if (!this.options.extractAllRuleDataPoints) {
      filteredRuleDataPoints = this.data.filter(item => ['NO_STOP', 'SOFT_STOP', 'HARD_STOP', 'MANUAL'].includes(item.fieldData.outcome))
    }

    this.currentRuleDataPoints = filteredRuleDataPoints.flatMap(item => {
      const isManual = item.fieldData.outcome === 'MANUAL'
      return item.fieldData.data_points?.map(dp => ({
        ...dp,
        isManual,
        outcome: item.fieldData.outcome,
        displayOutcome: item.fieldData.display_outcome,
      })) || []
    })
  }

  /**
   * Process data points and categorize them
   */
  processDataPoints() {
    this.currentRuleDataPoints.forEach(dp => {
      const globalField = this.globalFields.find(gf => gf._id === dp.field_id)

      if (globalField?.path) {
        if (this.isSalesComparable(dp)) {
          this.processSalesComparableDataPoint(dp, globalField)
        } else {
          this.processRegularDataPoint(dp, globalField)
        }
      }
    })
  }

  /**
   * Check if data point is a sales comparable
   */
  isSalesComparable(dp) {
    const comparableInfo = this.extractComparableInfo(dp.name)
    return comparableInfo !== null
  }

  /**
   * Extract comparable information from data point name
   * @param {string} name - The data point name
   * @returns {Object|null} - Object with match and comparableNumber, or null if not a comparable
   */
  extractComparableInfo(name) {
    const salesComparableMatch = name?.match(/Sales Comparables #(\d+)/i)
    const isSubject = name?.startsWith('Subject - ')

    if (salesComparableMatch) {
      return { match: salesComparableMatch, comparableNumber: parseInt(salesComparableMatch[1], 10) }
    }

    if (isSubject) {
      return { match: ['Subject - ', '0'], comparableNumber: 0 }
    }

    return null
  }

  /**
   * Process sales comparable data point
   */
  processSalesComparableDataPoint(dp, globalField) {
    const comparableInfo = this.extractComparableInfo(dp.name)

    if (comparableInfo) {
      if (!this.salesComparableMap[globalField.path]) {
        this.salesComparableMap[globalField.path] = new Map()
      }

      const existingDpInfo = this.salesComparableMap[globalField.path].get(comparableInfo.comparableNumber)

      // If there's already a value for this comparable, prioritize failure outcomes
      // This handles cases where multiple rules have the same field_id + comparableNumber
      if (existingDpInfo) {
        const failureOutcomes = ['Failed', 'Rejected', 'Error']
        const existingIsFailure = failureOutcomes.includes(existingDpInfo.displayOutcome)
        const newIsFailure = failureOutcomes.includes(dp.displayOutcome)

        // Only overwrite if new is a failure and existing is not
        // This ensures failures take priority over "Passed"
        if (!existingIsFailure && newIsFailure) {
          this.salesComparableMap[globalField.path].set(comparableInfo.comparableNumber, {
            field_id: dp.field_id,
            isManual: dp.isManual,
            outcome: dp.outcome,
            displayOutcome: dp.displayOutcome,
          })
        }
        // Otherwise keep existing (don't let "Passed" overwrite "Failed")
      } else {
        // No existing value, just set it
        this.salesComparableMap[globalField.path].set(comparableInfo.comparableNumber, {
          field_id: dp.field_id,
          isManual: dp.isManual,
          outcome: dp.outcome,
          displayOutcome: dp.displayOutcome,
        })
      }
    }
  }

  /**
   * Process regular data point
   */
  processRegularDataPoint(dp, globalField) {
    this.pathsToHighlight.add(globalField.path)

    if (!this.pathToDataPoints.has(globalField.path)) {
      this.pathToDataPoints.set(globalField.path, [])
    }
    this.pathToDataPoints.get(globalField.path).push(dp)
  }

  /**
   * Process PDF fields and create annotations
   */
  processPdfFields() {
    const sortedPageNumbers = this.getSortedPageNumbers()

    sortedPageNumbers.forEach(pageNumInt => {
      const pageNum = pageNumInt.toString()
      const fields = this.pdfParsingResult.result.pdf_page_fields[pageNum]
      const pageNumber = pageNumInt + 1

      fields.forEach(field => {
        this.processField(field, pageNumber)
      })
    })
  }

  /**
   * Get sorted page numbers from PDF parsing results
   */
  getSortedPageNumbers() {
    return Object.keys(this.pdfParsingResult.result.pdf_page_fields)
      .map(num => parseInt(num))
      .sort((a, b) => a - b)
  }

  /**
   * Process individual field
   */
  processField(field, pageNumber) {
    const matchingPath = this.findMatchingPath(field)

    if (matchingPath) {
      this.processRegularField(field, pageNumber, matchingPath)
    } else {
      this.processSalesComparableField(field, pageNumber)
    }
  }

  /**
   * Find matching path for field
   */
  findMatchingPath(field) {
    return Array.from(this.pathsToHighlight).find(path =>
      field.field_name?.startsWith(path)
    )
  }

  /**
   * Process regular field
   */
  processRegularField(field, pageNumber, matchingPath) {
    this.initializePageAnnotations(pageNumber)

    const matchingDataPoints = this.pathToDataPoints.get(matchingPath) || []

    if (matchingDataPoints.length > 0) {
      this.addFieldIdsToHighlighted(matchingDataPoints)
      this.createRegularFieldAnnotations(field, pageNumber, matchingDataPoints)
      this.storePageNumbers(matchingDataPoints, pageNumber)
    }
  }

  /**
   * Fix fields that don't have a sequence number based on the x position
   * This matches the logic in highlightingUtils.js
   * @param {Object} field - The field to fix
   * @param {number} pageNumber - The page number (1-indexed)
   * @param {string} matchingSalesPath - The matching sales path
   */
  fixMissingSequenceNumber(field, pageNumber, matchingSalesPath) {
    if (!matchingSalesPath || !this.salesComparableMap[matchingSalesPath] || field.sequence_number !== undefined) {
      return
    }

    // Find related fields by page (pdf_page_fields key is the page number)
    const fieldsByPage = {}
    let realSequenceNumber = 0
    for (const [pageNum, fields] of Object.entries(this.pdfParsingResult.result.pdf_page_fields)) {
      if (fields.some(f => f.field_name?.startsWith(matchingSalesPath))) {
        fieldsByPage[pageNum] = fields
          .filter(f => f.field_name?.startsWith(matchingSalesPath))
          .sort((a, b) => a.x - b.x)
          .map(f => {
            return {
              ...f,
              realSequenceNumber: realSequenceNumber++,
            }
          })
      }
    }

    // Get the sequence number of the current field
    const pageNumKey = (pageNumber - 1).toString()
    const fixedField = fieldsByPage[pageNumKey]?.find(
      f => f.field_name === field.field_name && f.value === field.value
    )

    // Set the sequence number to the current field index + 1
    if (fixedField) {
      field.sequence_number = fixedField.realSequenceNumber + 1
    }
  }

  /**
   * Process sales comparable field
   */
  processSalesComparableField(field, pageNumber) {
    const matchingSalesPath = this.findMatchingSalesPath(field)

    // Fix fields that don't have a sequence number based on the x position
    this.fixMissingSequenceNumber(field, pageNumber, matchingSalesPath)

    if (this.isValidSalesComparableField(matchingSalesPath, field)) {
      this.processValidSalesComparableField(field, pageNumber, matchingSalesPath)
    }
  }

  /**
   * Find matching sales path
   */
  findMatchingSalesPath(field) {
    return Object.keys(this.salesComparableMap).find(path =>
      field.field_name?.startsWith(path)
    )
  }

  /**
   * Check if sales comparable field is valid
   */
  isValidSalesComparableField(matchingSalesPath, field) {
    return (
      matchingSalesPath &&
      this.salesComparableMap[matchingSalesPath] &&
      field.sequence_number !== undefined
    )
  }

  /**
   * Process valid sales comparable field
   */
  processValidSalesComparableField(field, pageNumber, matchingSalesPath) {
    if (this.salesComparableMap[matchingSalesPath].has(field.sequence_number)) {
      this.initializePageAnnotations(pageNumber)

      const dpInfo = this.salesComparableMap[matchingSalesPath].get(field.sequence_number)

      this.highlightedIds.add(dpInfo.field_id)
      this.createSalesComparableAnnotations(field, pageNumber, dpInfo)
      this.storeSalesComparablePageNumbers(dpInfo, pageNumber, field.sequence_number)
    }
  }

  /**
   * Initialize page annotations if needed
   */
  initializePageAnnotations(pageNumber) {
    if (!this.annotationsObject[pageNumber]) {
      this.annotationsObject[pageNumber] = []
    }
  }

  /**
   * Add field IDs to highlighted set
   */
  addFieldIdsToHighlighted(dataPoints) {
    dataPoints.forEach(dp => {
      this.highlightedIds.add(dp.field_id)
    })
  }

  /**
   * Create annotations for regular fields
   */
  createRegularFieldAnnotations(field, pageNumber, matchingDataPoints) {
    const hasManual = matchingDataPoints.some(dp => dp.isManual)
    // Get the outcome from the first matching data point
    const outcome = matchingDataPoints[0]?.outcome
    const displayOutcome = matchingDataPoints[0]?.displayOutcome
    field.rectangles.forEach(rect => {
      this.annotationsObject[pageNumber].push({
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        isManual: hasManual,
        outcome: outcome,
        displayOutcome: displayOutcome,
        field_id: matchingDataPoints[0].field_id,
        page_number: pageNumber,
        sequence_number: field.sequence_number,
      })
    })
  }

  /**
   * Create annotations for sales comparable fields
   */
  createSalesComparableAnnotations(field, pageNumber, dpInfo) {
    if (field.rectangles) {
      field.rectangles.forEach(rect => {
        this.annotationsObject[pageNumber].push({
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          isManual: dpInfo.isManual,
          outcome: dpInfo.outcome,
          displayOutcome: dpInfo.displayOutcome,
          field_id: dpInfo.field_id,
          page_number: pageNumber,
          sequence_number: field.sequence_number,
        })
      })
    } else if (field.x !== undefined) {
      this.annotationsObject[pageNumber].push({
        x: field.x,
        y: field.y,
        width: field.width,
        height: field.height,
        isManual: dpInfo.isManual,
        outcome: dpInfo.outcome,
        displayOutcome: dpInfo.displayOutcome,
        field_id: dpInfo.field_id,
        page_number: pageNumber,
        sequence_number: field.sequence_number,
      })
    }
  }

  /**
   * Store page numbers for regular fields
   */
  storePageNumbers(dataPoints, pageNumber) {
    dataPoints.forEach(dp => {
      if (!this.idToPage[dp.field_id]) {
        this.idToPage[dp.field_id] = pageNumber
      }
    })
  }

  /**
   * Store page numbers for sales comparable fields
   */
  storeSalesComparablePageNumbers(dpInfo, pageNumber, sequenceNumber) {
    if (!this.idToPage[dpInfo.field_id]) {
      this.idToPage[dpInfo.field_id] = pageNumber
    }

    const comparableKey = buildComparableKey(dpInfo.field_id, sequenceNumber)
    this.comparableToPage[comparableKey] = pageNumber
  }
}
